/**
 * DecryptionManager.ts
 *
 * Manages client-side RSA key generation and hybrid decryption for secure
 * communication with the backend server.
 *
 * Encryption scheme (hybrid RSA-OAEP + AES-256-GCM):
 *   - Backend generates a random AES-256 key, encrypts the message with AES-GCM,
 *     then encrypts the AES key with the client's RSA-OAEP public key.
 *   - This class decrypts in reverse: RSA-OAEP unwraps the AES key, then AES-GCM
 *     decrypts the message.
 *
 * Usage:
 *   1. Create an instance when your app loads
 *   2. Call generateKeypair() to create ephemeral keys
 *   3. Use getPublicKeyPEM() to get the public key to send to the server
 *   4. Use decrypt() to decrypt hybrid-encrypted responses from the server
 */

export class DecryptionManager {
  private keypair: CryptoKeyPair | null = null;
  private publicKeyPEM: string | null = null;

  /**
   * Generates a new RSA-OAEP keypair for this session.
   * This should be called once when the app loads or when starting a new session.
   *
   * @returns Promise that resolves when keypair is generated
   */
  async generateKeypair(): Promise<void> {
    try {
      // Generate RSA-OAEP keypair using Web Crypto API
      this.keypair = await window.crypto.subtle.generateKey(
        {
          name: "RSA-OAEP",
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: "SHA-256",
        },
        true, // extractable
        ["encrypt", "decrypt"]
      );

      // Export the public key to PEM format for sending to server
      const exportedPublicKey = await window.crypto.subtle.exportKey(
        "spki",
        this.keypair.publicKey
      );

      // Convert to PEM format
      this.publicKeyPEM = this.arrayBufferToPEM(exportedPublicKey, "PUBLIC KEY");

      console.log("✅ RSA keypair generated successfully");
    } catch (error) {
      console.error("❌ Failed to generate keypair:", error);
      throw new Error("Failed to generate encryption keypair");
    }
  }

  /**
   * Gets the public key in PEM format to send to the server.
   *
   * @returns The public key in PEM format
   * @throws Error if keypair hasn't been generated yet
   */
  getPublicKeyPEM(): string {
    if (!this.publicKeyPEM) {
      throw new Error("Keypair not generated. Call generateKeypair() first.");
    }
    return this.publicKeyPEM;
  }

  /**
   * Decrypts a hybrid-encrypted message from the server.
   *
   * The server sends a base64-encoded JSON payload containing:
   *   - encrypted_key: RSA-OAEP encrypted AES-256 key (base64)
   *   - nonce: 12-byte AES-GCM nonce/IV (base64)
   *   - ciphertext: AES-256-GCM encrypted message with appended auth tag (base64)
   *
   * Decryption steps:
   *   1. Parse the JSON payload
   *   2. RSA-OAEP decrypt the AES key using the ephemeral private key
   *   3. Import the AES key into Web Crypto
   *   4. AES-GCM decrypt the message using the recovered key + nonce
   *
   * @param encryptedBase64 - The hybrid-encrypted payload in base64 format
   * @returns Promise that resolves to the decrypted plaintext string
   * @throws Error if decryption fails or keypair not available
   */
  async decrypt(encryptedBase64: string): Promise<string> {
    if (!this.keypair) {
      throw new Error("Keypair not generated. Call generateKeypair() first.");
    }

    if (!encryptedBase64 || encryptedBase64.trim() === "") {
      return ""; // Return empty string for empty input
    }

    try {
      // --- Step 1: Decode the outer base64 and parse the JSON payload ---
      const payloadJson = atob(encryptedBase64);
      const payload = JSON.parse(payloadJson);

      const encryptedKeyB64: string = payload.encrypted_key;
      const nonceB64: string = payload.nonce;
      const ciphertextB64: string = payload.ciphertext;

      // --- Step 2: Decode each component from base64 ---
      const encryptedAesKey = this.base64ToArrayBuffer(encryptedKeyB64);
      const nonce = this.base64ToArrayBuffer(nonceB64);
      const ciphertext = this.base64ToArrayBuffer(ciphertextB64);

      // --- Step 3: RSA-OAEP decrypt the AES key ---
      const aesKeyRaw = await window.crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        this.keypair.privateKey,
        encryptedAesKey
      );

      // --- Step 4: Import the raw AES key into Web Crypto ---
      const aesKey = await window.crypto.subtle.importKey(
        "raw",
        aesKeyRaw,
        { name: "AES-GCM" },
        false, // not extractable
        ["decrypt"]
      );

      // --- Step 5: AES-GCM decrypt the message ---
      const decryptedData = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: nonce,
          // tagLength: 128 is the default and matches Python's AESGCM
        },
        aesKey,
        ciphertext
      );

      // --- Step 6: Convert ArrayBuffer to string ---
      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.error("❌ Hybrid decryption failed:", error);
      throw new Error("Failed to decrypt message");
    }
  }

  /**
   * Checks if the keypair has been generated.
   *
   * @returns true if keypair is available, false otherwise
   */
  isReady(): boolean {
    return this.keypair !== null && this.publicKeyPEM !== null;
  }

  /**
   * Clears the keypair from memory (for cleanup).
   * Call this when the user logs out or the session ends.
   */
  clearKeys(): void {
    this.keypair = null;
    this.publicKeyPEM = null;
    console.log("🔒 Keypair cleared from memory");
  }

  // ==================== Helper Methods ====================

  /**
   * Converts an ArrayBuffer to PEM format.
   */
  private arrayBufferToPEM(buffer: ArrayBuffer, label: string): string {
    const base64 = this.arrayBufferToBase64(buffer);
    const pemLines: string[] = [];

    pemLines.push(`-----BEGIN ${label}-----`);

    // Split base64 into 64-character lines
    for (let i = 0; i < base64.length; i += 64) {
      pemLines.push(base64.substring(i, i + 64));
    }

    pemLines.push(`-----END ${label}-----`);

    return pemLines.join("\n");
  }

  /**
   * Converts an ArrayBuffer to base64 string.
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Converts a base64 string to ArrayBuffer.
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}